import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, fetchSmsMediaBlob, observationImagePath } from '../api';
import { SmsComposer, SmsMedia } from '../components/SmsChat';

export interface ComposeTarget { phone: string; customerId: number | null; name?: string | null }

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};
const btn: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-3)',
  backgroundColor: 'transparent',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontWeight: 600,
  fontSize: 'var(--font-size-xs)',
  cursor: 'pointer',
};
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-background)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontFamily: 'inherit',
};

const CATEGORY: Record<string, { label: string; color: string; bg: string }> = {
  quote_request: { label: 'Quote request', color: 'var(--color-accent)', bg: 'var(--color-accent-muted)' },
  quote_accept: { label: 'Quote accepted', color: 'var(--color-accent)', bg: 'var(--color-accent-muted)' },
  quote_question: { label: 'Quote question', color: 'var(--color-accent)', bg: 'var(--color-accent-muted)' },
  email_confirmation: { label: 'Confirmation', color: 'var(--color-text-muted)', bg: 'rgba(108,117,125,0.12)' },
  quote_related: { label: 'Quote-related', color: 'var(--color-accent)', bg: 'var(--color-accent-muted)' },
};
function catBadge(category: string) {
  const c = CATEGORY[category] ?? { label: category, color: 'var(--color-text-muted)', bg: 'rgba(108,117,125,0.12)' };
  return <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: c.color, backgroundColor: c.bg, borderRadius: 4, padding: '1px 6px' }}>{c.label}</span>;
}
function fmt(ts: string | null) {
  if (!ts) return '';
  return new Date(ts).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function Assistant() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const composePhone = params.get('smsPhone');
  const composeTarget: ComposeTarget | null = composePhone
    ? { phone: composePhone, customerId: params.get('smsCustomerId') ? Number(params.get('smsCustomerId')) : null, name: params.get('smsName') }
    : null;
  const [view, setView] = useState<'drafts' | 'collected' | 'sms'>(composeTarget ? 'sms' : 'drafts');
  const { data: drafts } = useQuery({ queryKey: ['assistant-drafts'], queryFn: () => api.listAssistantDrafts('pending'), refetchInterval: 30_000 });
  const { data: smsDrafts } = useQuery({ queryKey: ['sms-drafts'], queryFn: () => api.listSmsDrafts('pending'), refetchInterval: 30_000 });
  // Same queryKey CollectedFeed itself uses when its status filter is 'new' (the
  // default) — React Query dedupes/shares the fetch, so this doesn't add a
  // second request when the Collected tab is open.
  // staleTime:0 + refetchIntervalInBackground override the global 5-minute
  // staleTime default (main.tsx) — without them, refetchOnWindowFocus is a
  // no-op for up to 5 minutes after the last fetch (data isn't "stale" yet),
  // which is the actual reason this used to require a manual page reload.
  const { data: newObservations } = useQuery({ queryKey: ['assistant-observations', 'new'], queryFn: () => api.listAssistantObservations('new'), refetchInterval: 30_000, staleTime: 0, refetchIntervalInBackground: true });
  const pending = drafts?.pending ?? 0;
  const smsPending = smsDrafts?.pending ?? 0;
  const collectedPending = newObservations?.length ?? 0;

  const tab = (v: typeof view) => ({ ...btn, ...(view === v ? { background: 'var(--color-accent)', color: '#fff', borderColor: 'var(--color-accent)' } : {}) });

  return (
    <div style={{ padding: 'var(--spacing-4)', maxWidth: 900 }}>
      <h1 style={{ margin: 0 }}>Assistant</h1>
      <p style={{ color: 'var(--color-text-muted)', margin: '4px 0 var(--spacing-3)' }}>
        Watches the inbox for quote-related emails and inbound texts, drafts a reply with the customer's history and
        live availability, and learns from your feedback. You can <strong>Approve &amp; Send</strong>, Approve only
        (no send), or 👎 with the correct answer so it improves.
      </p>
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-3)', flexWrap: 'wrap' }}>
        <button style={tab('drafts')} onClick={() => setView('drafts')}>
          Email drafts{pending ? ` (${pending})` : ''}
        </button>
        <button style={tab('sms')} onClick={() => setView('sms')}>
          SMS{smsPending ? ` (${smsPending})` : ''}
        </button>
        <button style={tab('collected')} onClick={() => setView('collected')}>
          Collected{collectedPending ? ` (${collectedPending})` : ''}
        </button>
      </div>
      {view === 'drafts' && <DraftsInbox onChange={() => qc.invalidateQueries({ queryKey: ['assistant-drafts'] })} />}
      {view === 'sms' && <SmsSection onChange={() => qc.invalidateQueries({ queryKey: ['sms-drafts'] })} composeTarget={composeTarget} />}
      {view === 'collected' && <CollectedFeed />}
    </div>
  );
}

function DraftsInbox({ onChange }: { onChange: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['assistant-drafts'], queryFn: () => api.listAssistantDrafts('pending'), refetchInterval: 30_000 });
  const review = useMutation({
    mutationFn: (v: { id: number; verdict: 'approve' | 'reject'; draft?: string; correction?: string; send?: boolean }) =>
      api.reviewAssistantDraft(v.id, { verdict: v.verdict, draft: v.draft, correction: v.correction, send: v.send }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['assistant-drafts'] }); onChange(); },
  });
  const items = data?.items ?? [];
  if (isLoading) return <section style={cardStyle}><p style={{ color: 'var(--color-text-muted)' }}>Loading…</p></section>;
  if (items.length === 0) return <section style={cardStyle}><p style={{ color: 'var(--color-text-muted)' }}>No drafts waiting. New quote emails get a draft within a few minutes. 🎉</p></section>;
  return (
    <div style={{ display: 'grid', gap: 'var(--spacing-3)' }}>
      {items.map((it) => (
        <DraftCard key={it.id} it={it} busy={review.isPending} onReview={(v) => review.mutate({ id: it.id, ...v })} />
      ))}
      {review.isError && (
        <p style={{ color: 'var(--color-error)' }}>{(review.error as Error).message}</p>
      )}
    </div>
  );
}

function DraftCard({
  it,
  busy,
  onReview,
}: {
  it: { id: number; fromName: string | null; fromEmail: string | null; customerId: number | null; customerName: string | null; subject: string | null; snippet: string | null; category: string; draftReply: string | null; receivedAt: string | null };
  busy: boolean;
  onReview: (v: { verdict: 'approve' | 'reject'; draft?: string; correction?: string; send?: boolean }) => void;
}) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(it.draftReply ?? '');
  const [rejecting, setRejecting] = useState(false);
  const [correction, setCorrection] = useState('');
  return (
    <section style={cardStyle}>
      <div style={{ marginBottom: 'var(--spacing-2)' }}>
        {catBadge(it.category)}{' '}
        {it.customerId ? (
          <button onClick={() => navigate(`/contacts?open=${it.customerId}`)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-accent)', fontWeight: 700, cursor: 'pointer' }} title="Open contact — quotes, invoices, Xero link">
            {it.customerName ?? it.fromName ?? it.fromEmail}
          </button>
        ) : (
          <strong>{it.customerName ?? it.fromName ?? it.fromEmail}</strong>
        )}
        <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}> · {fmt(it.receivedAt)}</span>
        {it.subject && <div style={{ fontWeight: 600, marginTop: 2 }}>{it.subject}</div>}
        {it.snippet && <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)', fontStyle: 'italic', marginTop: 2 }}>“{it.snippet.slice(0, 200)}{it.snippet.length > 200 ? '…' : ''}”</div>}
      </div>
      <label style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>Proposed reply (editable)</label>
      <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={8} style={{ ...inputStyle, marginTop: 4, marginBottom: 'var(--spacing-2)' }} />
      {!rejecting ? (
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
          <button
            style={{ ...btn, background: 'var(--color-accent)', color: '#fff', borderColor: 'var(--color-accent)' }}
            disabled={busy}
            onClick={() => onReview({ verdict: 'approve', draft, send: true })}
            title="Send this reply to the customer now, and learn from it"
          >
            ✅ Approve &amp; Send
          </button>
          <button
            style={btn}
            disabled={busy}
            onClick={() => onReview({ verdict: 'approve', draft, send: false })}
            title="Mark good and learn from it, but don't send (you'll send manually)"
          >
            👍 Approve only
          </button>
          <button style={btn} disabled={busy} onClick={() => setRejecting(true)} title="Not right — tell it the correct answer">
            👎 Needs work
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
          <textarea
            autoFocus
            value={correction}
            onChange={(e) => setCorrection(e.target.value)}
            rows={4}
            placeholder="What's the correct answer / what should it have said? The assistant learns from this."
            style={inputStyle}
          />
          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            <button style={{ ...btn, background: 'var(--color-error)', color: '#fff', borderColor: 'var(--color-error)' }} disabled={busy || !correction.trim()} onClick={() => onReview({ verdict: 'reject', correction: correction.trim() })}>
              Submit correction
            </button>
            <button style={btn} onClick={() => setRejecting(false)}>Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}

// Photos submitted with a website quote request. Same authenticated
// fetch-to-blob approach as SmsMedia/SmsMediaItem (SmsChat.tsx) — GCS objects
// stay private; the browser never sees a raw bucket URL.
function ObservationImageThumb({ observationId, token }: { observationId: number; token: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let active = true;
    let obj: string | null = null;
    fetchSmsMediaBlob(observationImagePath(observationId, token))
      .then((blob) => {
        if (!active) return;
        obj = URL.createObjectURL(blob);
        setSrc(obj);
      })
      .catch(() => {
        if (active) setErr(true);
      });
    return () => {
      active = false;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [observationId, token]);
  if (err) return <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>[image unavailable]</span>;
  if (!src) return <div style={{ width: 96, height: 96, background: 'var(--color-background)', border: '1px solid var(--color-border)', borderRadius: 6 }} />;
  return (
    <a href={src} target="_blank" rel="noreferrer" title="Open full size">
      <img
        src={src}
        alt="Photo submitted with the quote request"
        style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--color-border)', display: 'block' }}
      />
    </a>
  );
}

// Exported so Billing.tsx (the "New Quote Requests" panel) can reuse this
// without duplicating the authenticated fetch-to-blob logic.
export function ObservationImages({ observationId, tokens }: { observationId: number; tokens: string[] }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 'var(--spacing-2)' }}>
      {tokens.map((token) => (
        <ObservationImageThumb key={token} observationId={observationId} token={token} />
      ))}
    </div>
  );
}

function CollectedFeed() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'new' | 'archived' | 'all'>('new');
  const { data: items = [], isLoading } = useQuery({
    queryKey: ['assistant-observations', status],
    queryFn: () => api.listAssistantObservations(status),
    refetchInterval: 30_000,
  });
  const archive = useMutation({
    mutationFn: (id: number) => api.archiveAssistantObservation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assistant-observations'] }),
  });
  return (
    <section style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-1)', marginBottom: 'var(--spacing-2)' }}>
        {(['new', 'archived', 'all'] as const).map((s) => (
          <button key={s} style={{ ...btn, ...(status === s ? { background: 'var(--color-accent)', color: '#fff', borderColor: 'var(--color-accent)' } : {}) }} onClick={() => setStatus(s)}>
            {s === 'new' ? 'New' : s === 'archived' ? 'Archived' : 'All'}
          </button>
        ))}
      </div>
      {isLoading ? (
        <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>
      ) : items.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>Nothing collected yet.</p>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--spacing-1)' }}>
          {items.map((it, i) => (
            <div key={it.id} style={{ padding: 'var(--spacing-3)', borderTop: i === 0 ? 'none' : '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', gap: 'var(--spacing-3)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ marginBottom: 2 }}>
                  {catBadge(it.category)}{' '}
                  {it.customerId ? (
                    <button onClick={() => navigate(`/contacts?open=${it.customerId}`)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-accent)', fontWeight: 600, cursor: 'pointer' }}>{it.customerName ?? it.fromName ?? it.fromEmail}</button>
                  ) : (
                    <strong>{it.fromName ?? it.fromEmail ?? 'Unknown sender'}</strong>
                  )}
                  <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}> · {fmt(it.receivedAt)}</span>
                </div>
                {it.subject && <div style={{ fontWeight: 600 }}>{it.subject}</div>}
                {it.summary && (
                  <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', whiteSpace: 'pre-wrap' }}>
                    {it.summary}
                  </div>
                )}
                {it.images.length > 0 && <ObservationImages observationId={it.id} tokens={it.images} />}
              </div>
              {it.status === 'new' && <button style={btn} disabled={archive.isPending} onClick={() => archive.mutate(it.id)}>Archive</button>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── SMS section: AI-drafted replies to inbound texts + full conversations ───
function SmsSection({ onChange, composeTarget }: { onChange: () => void; composeTarget?: ComposeTarget | null }) {
  const [sub, setSub] = useState<'drafts' | 'threads'>(composeTarget ? 'threads' : 'drafts');
  const subBtn = (v: typeof sub) => ({ ...btn, ...(sub === v ? { background: 'var(--color-accent)', color: '#fff', borderColor: 'var(--color-accent)' } : {}) });
  return (
    <div style={{ display: 'grid', gap: 'var(--spacing-3)' }}>
      <div style={{ display: 'flex', gap: 'var(--spacing-1)' }}>
        <button style={subBtn('drafts')} onClick={() => setSub('drafts')}>Replies to approve</button>
        <button style={subBtn('threads')} onClick={() => setSub('threads')}>Conversations</button>
      </div>
      {composeTarget && (
        <section style={cardStyle}>
          <div style={{ fontWeight: 700, marginBottom: 'var(--spacing-1)' }}>New message</div>
          <SmsComposer phone={composeTarget.phone} customerId={composeTarget.customerId} contactName={composeTarget.name} />
        </section>
      )}
      {sub === 'drafts' ? <SmsDraftsInbox onChange={onChange} /> : <SmsThreads />}
    </div>
  );
}

function SmsDraftsInbox({ onChange }: { onChange: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['sms-drafts'], queryFn: () => api.listSmsDrafts('pending'), refetchInterval: 30_000 });
  const review = useMutation({
    mutationFn: (v: { id: number; verdict: 'approve' | 'reject'; draft?: string; correction?: string; send?: boolean }) =>
      api.reviewSmsDraft(v.id, { verdict: v.verdict, draft: v.draft, correction: v.correction, send: v.send }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sms-drafts'] });
      qc.invalidateQueries({ queryKey: ['sms-threads'] });
      onChange();
    },
  });
  const items = data?.items ?? [];
  if (isLoading) return <section style={cardStyle}><p style={{ color: 'var(--color-text-muted)' }}>Loading…</p></section>;
  if (items.length === 0) return <section style={cardStyle}><p style={{ color: 'var(--color-text-muted)' }}>No SMS replies waiting. New inbound texts get a draft within a few minutes. 🎉</p></section>;
  return (
    <div style={{ display: 'grid', gap: 'var(--spacing-3)' }}>
      {items.map((it) => (
        <SmsDraftCard key={it.id} it={it} busy={review.isPending} onReview={(v) => review.mutate({ id: it.id, ...v })} />
      ))}
      {review.isError && <p style={{ color: 'var(--color-error)' }}>{(review.error as Error).message}</p>}
    </div>
  );
}

function SmsDraftCard({
  it,
  busy,
  onReview,
}: {
  it: { id: number; phone: string; customerId: number | null; customerName: string | null; draftReply: string | null; inboundBody: string | null; receivedAt: string | null };
  busy: boolean;
  onReview: (v: { verdict: 'approve' | 'reject'; draft?: string; correction?: string; send?: boolean }) => void;
}) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(it.draftReply ?? '');
  const [rejecting, setRejecting] = useState(false);
  const [correction, setCorrection] = useState('');
  const who = it.customerName ?? it.phone;
  return (
    <section style={cardStyle}>
      <div style={{ marginBottom: 'var(--spacing-2)' }}>
        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-accent)', backgroundColor: 'var(--color-accent-muted)', borderRadius: 4, padding: '1px 6px' }}>SMS</span>{' '}
        {it.customerId ? (
          <button onClick={() => navigate(`/contacts?open=${it.customerId}`)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-accent)', fontWeight: 700, cursor: 'pointer' }} title="Open contact — quotes, invoices, Xero link">
            {who}
          </button>
        ) : (
          <strong>{who}</strong>
        )}
        <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}> · {it.phone} · {fmt(it.receivedAt)}</span>
        {it.inboundBody && <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', fontStyle: 'italic', marginTop: 4 }}>“{it.inboundBody}”</div>}
      </div>
      <label style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>Proposed reply (editable)</label>
      <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={4} style={{ ...inputStyle, marginTop: 4, marginBottom: 'var(--spacing-2)' }} />
      {!rejecting ? (
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
          <button
            style={{ ...btn, background: 'var(--color-accent)', color: '#fff', borderColor: 'var(--color-accent)' }}
            disabled={busy}
            onClick={() => onReview({ verdict: 'approve', draft, send: true })}
            title="Text this reply to the customer now, and learn from it"
          >
            ✅ Approve &amp; Send
          </button>
          <button style={btn} disabled={busy} onClick={() => onReview({ verdict: 'approve', draft, send: false })} title="Mark good and learn from it, but don't send">
            👍 Approve only
          </button>
          <button style={btn} disabled={busy} onClick={() => setRejecting(true)} title="Not right — tell it the correct answer">
            👎 Needs work
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
          <textarea autoFocus value={correction} onChange={(e) => setCorrection(e.target.value)} rows={3} placeholder="What should it have said? The assistant learns from this." style={inputStyle} />
          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            <button style={{ ...btn, background: 'var(--color-error)', color: '#fff', borderColor: 'var(--color-error)' }} disabled={busy || !correction.trim()} onClick={() => onReview({ verdict: 'reject', correction: correction.trim() })}>
              Submit correction
            </button>
            <button style={btn} onClick={() => setRejecting(false)}>Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}

function SmsThreads() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState<string | null>(null);
  const { data, isLoading } = useQuery({ queryKey: ['sms-threads'], queryFn: () => api.listSmsThreads(), refetchInterval: 30_000 });
  const markRead = useMutation({
    mutationFn: (phone: string) => api.markSmsThreadRead(phone),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sms-threads'] }),
  });
  const backfill = useMutation({
    mutationFn: () => api.backfillSms(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sms-threads'] });
      qc.invalidateQueries({ queryKey: ['sms-drafts'] });
    },
  });
  const threads = data?.threads ?? [];
  const importBtn = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-2)', gap: 'var(--spacing-2)' }}>
      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
        {backfill.data ? `Imported ${backfill.data.imported} (skipped ${backfill.data.skipped} already-known).` : 'Pull texts received before the webhook was set up.'}
      </span>
      <button style={btn} disabled={backfill.isPending} onClick={() => backfill.mutate()} title="Fetch recent inbound texts from Twilio's history">
        {backfill.isPending ? 'Importing…' : '↧ Import from Twilio'}
      </button>
    </div>
  );
  if (isLoading) return <section style={cardStyle}><p style={{ color: 'var(--color-text-muted)' }}>Loading…</p></section>;
  if (threads.length === 0) return <section style={cardStyle}>{importBtn}<p style={{ color: 'var(--color-text-muted)' }}>No conversations yet. Inbound texts to the business number appear here.</p></section>;
  return (
    <section style={cardStyle}>
      {importBtn}
      <div style={{ display: 'grid', gap: 'var(--spacing-1)' }}>
        {threads.map((t, i) => {
          const isOpen = open === t.phone;
          return (
            <div key={t.phone} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--color-border)', paddingTop: i === 0 ? 0 : 'var(--spacing-2)', marginTop: i === 0 ? 0 : 'var(--spacing-1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--spacing-2)', alignItems: 'baseline', cursor: 'pointer' }}
                   onClick={() => { const next = isOpen ? null : t.phone; setOpen(next); if (next && t.unread) markRead.mutate(t.phone); }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong>{t.customerName ?? t.phone}</strong>
                  {t.unread > 0 && <span style={{ marginLeft: 6, fontSize: 'var(--font-size-xs)', fontWeight: 700, color: '#fff', background: 'var(--color-error)', borderRadius: 10, padding: '0 6px' }}>{t.unread}</span>}
                  <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}> · {fmt(t.lastAt)}</span>
                  <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.lastDirection === 'out' ? '↩ ' : ''}{t.lastBody}
                  </div>
                </div>
                {t.customerId && (
                  <button style={btn} onClick={(e) => { e.stopPropagation(); navigate(`/contacts?open=${t.customerId}`); }}>Contact</button>
                )}
              </div>
              {isOpen && (
                <div style={{ margin: 'var(--spacing-2) 0' }}>
                  <div style={{ display: 'grid', gap: 4 }}>
                    {t.messages.map((m, idx) => (
                      <div key={idx} style={{ justifySelf: m.direction === 'in' ? 'start' : 'end', maxWidth: '80%', background: m.direction === 'in' ? 'var(--color-background)' : 'var(--color-accent-muted)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', padding: 'var(--spacing-1) var(--spacing-2)' }}>
                        {m.body && <div style={{ fontSize: 'var(--font-size-sm)' }}>{m.body}</div>}
                        {m.media && m.media.length > 0 && <SmsMedia media={m.media} />}
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textAlign: m.direction === 'in' ? 'left' : 'right' }}>{fmt(m.at)}{m.direction === 'out' && m.status ? ` · ${m.status}` : ''}</div>
                      </div>
                    ))}
                  </div>
                  <SmsComposer phone={t.phone} customerId={t.customerId} contactName={t.customerName} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
